"use client";

import confirmIdentity from "@faire/web-api/routes/confirm-identity";
import expandingBusinessesOffer from "@faire/web-api/routes/expanding-businesses-offer";
import expandingBusinessesOfferApply from "@faire/web-api/routes/expanding-businesses-offer/apply";
import smallBusinessGrant from "@faire/web-api/routes/small-business-grant";
import insiderTermsOfService from "@faire/web-api/routes/tos-insider";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { matchPathWithLocale } from "@faire/web/ui/routing/react-utils";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import once from "lodash/once";
import React, { Suspense, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LazyPopupOptInNudge } from "@faire/retailer/components/App/PopupOptInNudge";
import { shouldShowMobileAppBanner } from "@faire/retailer/components/App/shouldShowMobileAppBanner";
import { useHeaderPriorityStore } from "@faire/retailer/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { useIsHeaderDropdownVisible } from "@faire/retailer/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { LazyAbandonedBrandCartNudge } from "@faire/retailer/features/Nudges/AbandonedBrandCartNudge/LazyAbandonedBrandCartNudge";
import { LazyKnownReferrerNudge } from "@faire/retailer/features/Nudges/KnownReferrerNudge/LazyKnownReferrerNudge";
import { useKnownReferrerNudgeEligibility } from "@faire/retailer/features/Nudges/KnownReferrerNudge/useKnownReferrerNudgeEligibility";
import { useInsiderTermsAndConditionsConsentStatus } from "@faire/retailer/hooks/insider/useInsiderTermsAndConditionsConsentStatus";
import { useShopifyOfferEligibility } from "@faire/retailer/hooks/shopifyOnboarding/useShopifyOfferEligibility";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getSettingEnableRetailerNotification } from "@faire/retailer/settings/getSettingEnableRetailerNotification";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

import {
  shouldAbandonedBrandCartNudge,
  useAbandonedBrandCartNudge,
} from "./AbandonedBrandCartNudge/useAbandonedBrandCartNudge";
import { LazyCheckoutNudge } from "./CheckoutNudge";
import { useCheckoutNudgeStore } from "./CheckoutNudge/CheckoutNudgeStore";
import { useHasFaireDirectIncentiveNudge } from "./CheckoutNudge/FaireDirectIncentiveNudge/useHasFaireDirectIncentiveNudge";
import { LazyMarketplaceIncentiveNudge } from "./MarketplaceIncentiveNudge/LazyMarketplaceIncentiveNudge";
import { useMarketplaceIncentiveNudge } from "./MarketplaceIncentiveNudge/useMarketplaceIncentiveNudge";
import { LazyOwfUpsellNudge } from "./OpenWithFaireNudge/LazyOwFUpsellNudge";
import { useOwFNudgeEligibility } from "./OpenWithFaireNudge/useOwFNudgeEligibility";
import { LazyReferralAdoptionNudge } from "./ReferralAdoptionNudges/LazyReferralAdoptionNudge";
import { useReferralAdoptionNudgeEligibility } from "./ReferralAdoptionNudges/useReferralAdoptionNudgeEligibility";
import { LazyReferralReengageNudge } from "./ReferralReengageNudge/LazyReferralReengageNudge";
import { useReengageReferralNudgeEligibility } from "./ReferralReengageNudge/useReferralReengageNudgeEligibility";
import { LazyShopifyPlanUpsellNudge } from "./ShopifyPlanUpsellNudge";
import { isPathEligibleForShopifyNudge } from "./ShopifyPlanUpsellNudge/isPathEligibleForShopifyNudge";
import { AppNudgeProvider } from "./useAppNudgeStore";

export const AppNudges: React.FC = () => {
  const history = useHistory();
  const { isAnyHeaderDropdownVisible } = useIsHeaderDropdownVisible();
  const [{ isNotificationPreviewVisible }] = useHeaderPriorityStore([
    "isNotificationPreviewVisible",
  ]);

  const isHeaderComponentVisible =
    (isAnyHeaderDropdownVisible || isNotificationPreviewVisible) &&
    getSettingEnableRetailerNotification();

  const { userCanPlaceOrder: userCanAcceptTerms } = useUserStore([
    "userCanPlaceOrder",
  ]);

  const isInsiderTermsAndConditionsPage = matchPathWithLocale(
    history.location.pathname,
    insiderTermsOfService()
  );

  const { isInsiderTermsAndConditionsPendingOrOutdated } =
    useInsiderTermsAndConditionsConsentStatus();

  /* If the user is in the Insider terms and conditions page and their insider terms and conditions consent is pending or outdated.
   * We'll show the insider terms and conditions consent banner in the Insider terms and conditions page.
   * And we'll hide all nudges to not deviate the user from the Insider terms and conditions consent flow. */
  const shouldHideNudges =
    (isInsiderTermsAndConditionsPage &&
      isInsiderTermsAndConditionsPendingOrOutdated &&
      userCanAcceptTerms) ||
    isHeaderComponentVisible;

  const blockedPages = [
    confirmIdentity(),
    expandingBusinessesOffer(),
    expandingBusinessesOfferApply(),
    smallBusinessGrant(),
  ];

  const isBlockedPage = blockedPages.some((blockedPage: string) => {
    return matchPathWithLocale(history.location.pathname, blockedPage);
  });

  if (shouldHideNudges || isBlockedPage) {
    return null;
  }

  return (
    <AppNudgeProvider>
      <AppNudgesBase />
    </AppNudgeProvider>
  );
};

const AppNudgesBase: React.FC = () => {
  const { pathname: pathnameWithMaybeLocale } = useLocation();
  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );
  const { isMobile, isTabletAndAbove } = useViewport();
  const abandonedBrandCartNudge = useAbandonedBrandCartNudge();

  const { shouldShowOwFNudge, hasAcknowledgedOpenWithFaireNudge } =
    useOwFNudgeEligibility(pathname);

  const { shouldShowKnownReferrerNudge, hasAcknowledgedKnownReferrerNudge } =
    useKnownReferrerNudgeEligibility();

  const shouldRenderAbandonedBrandCartNudge =
    abandonedBrandCartNudge.hasNudge && shouldAbandonedBrandCartNudge(pathname);

  const shouldRenderCheckoutNudge = useShouldRenderCheckoutNudge(pathname);

  const isMobileAppBannerShown = once(() =>
    shouldShowMobileAppBanner(pathname)
  );
  const shouldRenderPopupOptInNudge =
    isLoggedInRetailer() && !(isMobile && isMobileAppBannerShown());

  const {
    shouldShowReengageReferralNudge,
    hasAcknowledgedReengageReferralNudge,
  } = useReengageReferralNudgeEligibility();

  const { showEligibleShopifyOfferIncentives } = useShopifyOfferEligibility();

  const { shouldShowMarketplaceIncentiveNudge } =
    useMarketplaceIncentiveNudge();

  const {
    shouldShowReferralAdoptionNudge,
    hasAcknowledgedReferralAdoptionNudge,
  } = useReferralAdoptionNudgeEligibility();

  if (shouldShowMarketplaceIncentiveNudge) {
    return (
      <Suspense fallback={null}>
        <LazyMarketplaceIncentiveNudge />
      </Suspense>
    );
  }

  if (
    showEligibleShopifyOfferIncentives &&
    isPathEligibleForShopifyNudge(pathname) &&
    isTabletAndAbove
  ) {
    return (
      <Suspense fallback={null}>
        <LazyShopifyPlanUpsellNudge />
      </Suspense>
    );
  }

  if (
    hasAcknowledgedOpenWithFaireNudge ||
    hasAcknowledgedKnownReferrerNudge ||
    hasAcknowledgedReferralAdoptionNudge ||
    hasAcknowledgedReengageReferralNudge
  ) {
    // Block nudges until next session after seeing OwF nudge
    return null;
  }

  if (shouldShowOwFNudge) {
    return (
      <Suspense fallback={null}>
        <LazyOwfUpsellNudge pathname={pathname} />
      </Suspense>
    );
  }

  if (shouldShowReengageReferralNudge) {
    return (
      <Suspense fallback={null}>
        <LazyReferralReengageNudge />
      </Suspense>
    );
  }

  if (shouldShowKnownReferrerNudge) {
    return (
      <Suspense fallback={null}>
        <LazyKnownReferrerNudge />
      </Suspense>
    );
  }

  if (shouldShowReferralAdoptionNudge) {
    return (
      <Suspense fallback={null}>
        <LazyReferralAdoptionNudge />
      </Suspense>
    );
  }

  if (shouldRenderAbandonedBrandCartNudge) {
    return (
      <Suspense fallback={null}>
        <LazyAbandonedBrandCartNudge
          abandonedBrandCartNudge={abandonedBrandCartNudge}
        />
      </Suspense>
    );
  }

  if (shouldRenderCheckoutNudge) {
    return (
      <Suspense fallback={null}>
        <LazyCheckoutNudge />
      </Suspense>
    );
  }

  if (shouldRenderPopupOptInNudge) {
    return (
      <Suspense fallback={null}>
        <LazyPopupOptInNudge />
      </Suspense>
    );
  }

  return null;
};

const useShouldRenderCheckoutNudge = (pathname: string) => {
  const {
    shouldShowNudge: shouldShowCheckoutNudge,
    checkoutNudgedInCurrentPage,
  } = useCheckoutNudgeStore(["shouldShowNudge", "checkoutNudgedInCurrentPage"]);

  const hasFaireDirectIncentiveNudge = useHasFaireDirectIncentiveNudge();

  return (
    (shouldShowCheckoutNudge(pathname) && hasFaireDirectIncentiveNudge) ||
    checkoutNudgedInCurrentPage
  );
};
